import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import useSWR from "swr";
import { wrappedCollectionFetcher } from "services/api.service";
import { LocationManagementLocation } from "types/locationManagement/location";

type Option = { id: string; code: string; warehouse?: boolean };

interface Props {
  name: string;
  defaultValue?: Option | null;
  excludeId?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  onChange?: (parentId: string) => void;
  onCommit?: () => void;
}

const WAREHOUSE_LOCATIONS_KEY = "/location_management/locations?page[size]=50&filter[]=warehouse_id,is_not_empty";

const locationsKey = (query: string) => {
  const filter = query ? `&filter[]=${encodeURIComponent(`code,contains,${query}`)}` : "";
  return `/location_management/locations?page[size]=25${filter}`;
};

const fetchLocationOptions = (url: string) =>
  wrappedCollectionFetcher<LocationManagementLocation>(url).then((res) => res.rows || []);

const toOption = (location: LocationManagementLocation): Option => ({
  id: String(location.id),
  code: location.code,
  warehouse: Boolean(location.warehouseId || location.warehouse),
});

export const ParentLocationPicker: React.FC<Props> = ({
  name,
  defaultValue = null,
  excludeId,
  disabled = false,
  inputRef,
  onChange,
  onCommit,
}) => {
  const [value, setValue] = useState<Option | null>(defaultValue);
  const [query, setQuery] = useState("");
  const defaultId = defaultValue?.id ?? "";
  const defaultCode = defaultValue?.code ?? "";
  const defaultWarehouse = Boolean(defaultValue?.warehouse);
  // A scan can press Enter before the matching location has loaded. Ignore the late response once a newer choice wins.
  const lookupRef = useRef(0);
  const mountedRef = useRef(true);
  const hiddenRef = useRef<HTMLInputElement>(null);
  // Create or Update blurs this field and submits immediately. Hold that submit while a code is still being looked up.
  const pendingLookup = useRef<Promise<void> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    lookupRef.current += 1;
    pendingLookup.current = null;
    setValue(defaultId ? { id: defaultId, code: defaultCode, warehouse: defaultWarehouse } : null);
    setQuery("");
  }, [defaultId, defaultCode, defaultWarehouse, excludeId]);

  useEffect(() => {
    const form = hiddenRef.current?.form;
    if (!form) return undefined;
    let waiting = false;
    const holdSubmit = (event: Event) => {
      const lookup = pendingLookup.current;
      if (!lookup) return;
      event.preventDefault();
      event.stopPropagation();
      if (waiting) return;
      waiting = true;
      lookup.finally(() => {
        waiting = false;
        if (!mountedRef.current || !form.isConnected) return;
        form.requestSubmit();
      });
    };
    form.addEventListener("submit", holdSubmit, true);
    return () => form.removeEventListener("submit", holdSubmit, true);
  }, []);

  const { data, isLoading } = useSWR(disabled ? null : locationsKey(query), fetchLocationOptions, {
    keepPreviousData: true,
    revalidateOnFocus: false,
    // The first page is the same on every location. Keep it briefly so moving between locations does not fetch it again.
    dedupingInterval: query ? 2_000 : 60_000,
  });
  const { data: warehouseRows } = useSWR(disabled ? null : WAREHOUSE_LOCATIONS_KEY, fetchLocationOptions, {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
  });

  const visible = (location: LocationManagementLocation) => String(location.id) !== String(excludeId);
  const warehouseOptions = (warehouseRows || [])
    .filter(visible)
    .map(toOption)
    .filter((option) => !query || option.code.toLowerCase().includes(query.toLowerCase()));
  const seen = new Set(warehouseOptions.map((option) => option.id));
  const options = [
    ...warehouseOptions,
    ...(data || [])
      .filter(visible)
      .map(toOption)
      .filter((option) => !seen.has(option.id)),
  ];

  const writeParentId = (id: string) => {
    if (hiddenRef.current) hiddenRef.current.value = id;
  };

  const choose = (option: Option | null) => {
    lookupRef.current += 1;
    pendingLookup.current = null;
    setValue(option);
    writeParentId(option?.id ?? "");
    onChange?.(option?.id ?? "");
  };

  // The typed text is only a search. Enter (and a scanner newline) has to copy an exact code into the hidden id.
  const optionForCode = (raw: string) => {
    const normalized = raw
      .replace(/[\r\n]/g, "")
      .trim()
      .toLowerCase();
    if (!normalized) return null;
    const candidates = [...options, ...(warehouseRows || []).filter(visible).map(toOption)];
    return candidates.find((option) => option.code.toLowerCase() === normalized) ?? null;
  };

  const commitTypedCode = (raw: string) => {
    const code = raw.replace(/[\r\n]/g, "").trim();
    if (!code) return;
    const selectedLabel = value ? (value.warehouse ? `${value.code} (warehouse)` : value.code) : "";
    if (selectedLabel.toLowerCase() === code.toLowerCase()) {
      lookupRef.current += 1;
      pendingLookup.current = null;
      return;
    }
    const local = optionForCode(code);
    if (local) {
      if (local.id !== value?.id) choose(local);
      else {
        lookupRef.current += 1;
        pendingLookup.current = null;
      }
      return;
    }
    const request = (lookupRef.current += 1);
    const lookup = fetchLocationOptions(locationsKey(code))
      .then((rows) => {
        if (!mountedRef.current || request !== lookupRef.current) return;
        const match = rows
          .filter(visible)
          .map(toOption)
          .find((option) => option.code.toLowerCase() === code.toLowerCase());
        if (!match) return;
        // The hidden input is posted from the DOM. Set it now so the submit does not keep the previous parent.
        writeParentId(match.id);
        setValue(match);
        onChange?.(match.id);
      })
      .catch(() => {
        // A failed lookup leaves the saved parent in place. The field can be chosen again.
      })
      .finally(() => {
        if (pendingLookup.current === lookup) pendingLookup.current = null;
      });
    pendingLookup.current = lookup;
    // Blur clears the typed code before this id comes back. Count that parent now so a late
    // default cannot drop this request and file the location somewhere else.
    onCommit?.();
  };

  if (disabled) {
    return (
      <TextField
        label="Inside Location"
        value=""
        disabled
        fullWidth
        helperText="This warehouse location is the fixed parent for that building. It can't be moved."
      />
    );
  }

  return (
    <>
      <input ref={hiddenRef} type="hidden" name={name} value={value?.id ?? ""} />
      <Autocomplete
        value={value}
        options={options}
        loading={isLoading}
        onChange={(_event, option) => {
          choose(option);
        }}
        onInputChange={(_event, text, reason) => {
          if (reason === "clear") {
            setQuery("");
            return;
          }
          if (reason !== "input") return;
          const code = text.replace(/[\r\n]/g, "");
          setQuery(code);
          if (code.length !== text.length) commitTypedCode(code);
        }}
        getOptionLabel={(option) => (option.warehouse ? `${option.code} (warehouse)` : option.code)}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        filterOptions={(all) => all}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Inside Location"
            helperText="File it into a warehouse, or into another location. Leave empty if it isn't inside one."
            inputRef={inputRef}
            inputProps={{
              ...params.inputProps,
              onBlur: (event) => {
                commitTypedCode(event.currentTarget.value);
                params.inputProps.onBlur?.(event as React.FocusEvent<HTMLInputElement>);
              },
              onKeyDown: (event) => {
                params.inputProps.onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>);
                // A scan ends in Enter and never highlights a row. The list still handles Enter when one is highlighted.
                if (event.key !== "Enter" || event.defaultPrevented) return;
                commitTypedCode(event.currentTarget.value);
              },
            }}
          />
        )}
      />
    </>
  );
};
